import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, PageHeader, Alert } from '../../components/ui';
import api from '../../services/api';
import useAuthStore from '../../store/authStore';
import toast from 'react-hot-toast';

const PLANS = [
  { id: 'pack_one',      name: 'Pack One',      price: '€32',  period: '/month', contacts:'2,000',   sends:'6,000',   verify:'2,000',   accounts:'10' },
  { id: 'pack_two',      name: 'Pack Two',      price: '€78',  period: '/month', contacts:'30,000',  sends:'90,000',  verify:'30,000',  accounts:'100', popular:true },
  { id: 'pack_advanced', name: 'Pack Advanced', price: '€144', period: '/month', contacts:'Unlimited',sends:'150,000',verify:'50,000',  accounts:'Unlimited' },
];

export default function Billing() {
  const { user } = useAuthStore();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchHistory(); }, []);

  const fetchHistory = async () => {
    try { const r = await api.get('/payments/history'); setHistory(r.data.payments || []); } catch {}
  };

  const subscribe = async (planId) => {
    setLoading(true);
    try {
      const r = await api.post('/payments/create-order', { planId });
      if (!r.data.orderId) return toast.error(r.data.message || 'Payment gateway not configured yet. Contact support@msdiscoveries.com');
      // Razorpay checkout
      const options = {
        key: r.data.key, amount: r.data.amount, currency: r.data.currency,
        order_id: r.data.orderId, name: 'EmailMark by MS Discoveries',
        description: `${PLANS.find(p=>p.id===planId)?.name} Plan`,
        handler: async (response) => {
          try {
            await api.post('/payments/verify', { ...response, planId });
            toast.success('Payment successful! Account activated.'); window.location.reload();
          } catch { toast.error('Payment verification failed'); }
        },
      };
      if (window.Razorpay) { const rzp = new window.Razorpay(options); rzp.open(); }
      else toast.error('Payment gateway loading. Please try again.');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to create order'); }
    finally { setLoading(false); }
  };

  const daysLeft = () => {
    if (!user?.planExpiresAt) return 0;
    return Math.max(0, Math.ceil((new Date(user.planExpiresAt) - Date.now()) / (1000*60*60*24)));
  };

  return (
    <div>
      <PageHeader title="Billing" subtitle="Manage your subscription and payments" />

      {/* Current plan */}
      <Card style={{ marginBottom: 24, background: 'linear-gradient(135deg, rgba(46,204,113,0.06), rgba(46,204,113,0.02))', border: '1px solid rgba(46,204,113,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, color: '#555', fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Current Plan</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: '#2ECC71', marginBottom: 4 }}>{user?.planId?.replace(/_/g,' ').replace(/\b\w/g, c=>c.toUpperCase()) || 'No Plan'}</div>
            <div style={{ fontSize: 13, color: '#666' }}>
              {user?.planExpiresAt ? `Expires: ${new Date(user.planExpiresAt).toLocaleDateString()} (${daysLeft()} days left)` : 'No active subscription'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {[['Contacts', user?.contactsLimit === -1 ? '∞' : user?.contactsLimit?.toLocaleString()], ['Sends', user?.emailSendsLimit === -1 ? '∞' : user?.emailSendsLimit?.toLocaleString()], ['Verifications', user?.verifyLimit === -1 ? '∞' : user?.verifyLimit?.toLocaleString()], ['Accounts', user?.accountsLimit === -1 ? '∞' : user?.accountsLimit]].map(([l,v]) => (
              <div key={l} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 800 }}>{v?.toLocaleString?.() || v}</div>
                <div style={{ fontSize: 11, color: '#555' }}>{l}/mo</div>
              </div>
            ))}
          </div>
        </div>
        {user?.status === 'grace_period' && <Alert type="danger" style={{ marginTop: 16 }}>⚠️ Subscription expired. Renew now to restore full access.</Alert>}
      </Card>

      {/* Plans */}
      <h3 style={{ fontWeight: 800, marginBottom: 16, fontSize: 18 }}>Choose a Plan</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 32 }}>
        {PLANS.map(p => (
          <Card key={p.id} style={{ border: `1px solid ${p.popular ? '#2ECC71' : user?.planId === p.id ? 'rgba(46,204,113,0.4)' : 'rgba(46,204,113,0.1)'}`, position: 'relative', boxShadow: p.popular ? '0 0 30px rgba(46,204,113,0.1)' : 'none' }}>
            {p.popular && <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: '#2ECC71', color: '#000', padding: '3px 16px', borderRadius: 20, fontSize: 10, fontWeight: 900, whiteSpace: 'nowrap' }}>MOST POPULAR</div>}
            {user?.planId === p.id && <div style={{ position: 'absolute', top: -12, right: 16, background: '#3498DB', color: '#fff', padding: '3px 12px', borderRadius: 20, fontSize: 10, fontWeight: 900 }}>CURRENT</div>}
            <div style={{ fontSize: 12, color: '#2ECC71', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>{p.name}</div>
            <div style={{ fontSize: 36, fontWeight: 900, marginBottom: 4, letterSpacing: -1 }}>{p.price}<span style={{ fontSize: 13, color: '#555', fontWeight: 400 }}>{p.period}</span></div>
            <div style={{ fontSize: 12, color: '#555', marginBottom: 16 }}>
              {[`✉️ ${p.emails} emails`, `🔗 ${p.accounts} accounts`, `✅ ${p.verify} verifications`, `🌡️ ${p.warmup} warmup`].map(f => <div key={f} style={{ marginBottom: 4 }}>{f}</div>)}
            </div>
            <Button fullWidth variant={user?.planId === p.id ? 'secondary' : 'primary'} loading={loading} onClick={() => subscribe(p.id)}>
              {user?.planId === p.id ? '🔄 Renew' : 'Upgrade →'}
            </Button>
          </Card>
        ))}
      </div>

      {/* Payment history */}
      <Card>
        <h3 style={{ fontWeight: 700, marginBottom: 16 }}>Payment History</h3>
        {history.length === 0 ? <p style={{ color: '#555', fontSize: 14 }}>No payments yet.</p> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ borderBottom: '1px solid rgba(46,204,113,0.1)' }}>
              {['Plan','Amount','Status','Date'].map(h => <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#555', fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {history.map(p => (
                <tr key={p.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                  <td style={{ padding: '10px 12px', color: '#ccc' }}>{p.planId?.replace(/_/g,' ')}</td>
                  <td style={{ padding: '10px 12px', color: '#2ECC71', fontWeight: 700 }}>€{p.amount}</td>
                  <td style={{ padding: '10px 12px' }}><Badge color={p.status==='completed'?'success':p.status==='failed'?'danger':'warning'}>{p.status}</Badge></td>
                  <td style={{ padding: '10px 12px', color: '#555' }}>{p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
